import { useTranslation } from 'next-i18next';

import { Box, Button, Flex, Text } from '@chakra-ui/react';

import { AdaptiveDrawer } from '@nocowanie/common-ui/components/molecules/adaptive-drawer';
import { IconProps } from '@nocowanie/icons/common';

import { AmenitiesCategorized } from '@app/components/molecules/amenities-categorized';
import { AppModalsEnum } from '@app/enums';
import { getAmenityIcon } from '@app/helpers/get-amenity-icon';
import { useDrawerState } from '@app/hooks/useDrawerState';
import { useModalFragmentSync } from '@app/hooks/useModalFragmentSync';
import * as accommodationStaticSlice from '@app/state/slices/accommodation-static.slice';
import * as commonUiStateSlice from '@app/state/slices/common-ui-state.slice';
import { useAppDispatch, useAppSelector } from '@app/state/state';

import { AmenitiesMobileProps } from './amenities-mobile.props';

export const AmenitiesMobile = ({ internalAmenityGroupsToShow, ...rest }: AmenitiesMobileProps) => {
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'amenities',
    });
    const dispatch = useAppDispatch();

    const isDrawerOpen = useAppSelector(
        commonUiStateSlice.selectors.selectModalData<boolean>(AppModalsEnum.Amenities),
    );

    const { isDrawerPending, openDrawer } = useDrawerState(() => {
        dispatch(
            commonUiStateSlice.actions.setModalDataAction({
                modalName: AppModalsEnum.Amenities,
                modalData: true,
            }),
        );
    });

    const closeDrawer = () => {
        dispatch(commonUiStateSlice.actions.resetModalDataAction(AppModalsEnum.Amenities));
    };

    useModalFragmentSync(AppModalsEnum.Amenities, isDrawerOpen);

    const { services } = useAppSelector(accommodationStaticSlice.selectors.selectAmenitiesData);

    if (!internalAmenityGroupsToShow?.length || !services?.length) {
        return;
    }

    const iconConfig: IconProps = {
        size: 'md',
        variant: 'subtle',
        color: 'secondary.600',
    };

    return (
        <Box {...rest}>
            <Flex direction={'column'} gap={4}>
                {internalAmenityGroupsToShow.map(({ id, label, type, free }) => {
                    return (
                        <Flex key={id} gap={2}>
                            {getAmenityIcon(type, iconConfig)}
                            <Text>{free ? `${t('free')} ${label.toLowerCase()}` : label}</Text>
                        </Flex>
                    );
                })}
            </Flex>
            <Button
                onClick={openDrawer}
                w={'full'}
                colorPalette={'secondary'}
                mt={4}
                loading={isDrawerPending}
                disabled={isDrawerPending}
            >
                {t('seeAllAmenities', {
                    value: services.length,
                })}
            </Button>

            <AdaptiveDrawer
                isOpen={isDrawerOpen}
                onOpen={openDrawer}
                onClose={closeDrawer}
                header={t('amenitiesHeader')}
                display={'flex'}
                flexDirection={'column'}
                overflow={'auto'}
                p={4}
                mb={16}
                drawerProps={{
                    size: { base: 'full', md: 'lg' },
                    variant: 'withMargin',
                    placement: 'bottom',
                }}
            >
                <AmenitiesCategorized internalAmenityGroupsToShow={internalAmenityGroupsToShow} />
            </AdaptiveDrawer>
        </Box>
    );
};
